import { type BadgeId } from "./l4/mockWeek.js";
import { isMarkerAxis } from "./markers.js";
import { isShopLineKey } from "./shopKeys.js";
import { isUtcDay } from "./time.js";

/**
 * The identifier grammar of the learn API. These patterns are the single
 * source of truth: the contract builds its zod schemas from them and the
 * edge, the PWA and the lint all parse with the same functions, so a key one
 * side calls well-formed is well-formed everywhere. Shape is decided here
 * (400 on the wire); whether the id exists is the manifest's call (422).
 *
 * A slug (`red-wine-styles`) is written as "letters, digits and single hyphens,
 * ending in a letter or digit": two lookaheads rule out a leading and a doubled
 * hyphen. That is the language of `[a-z0-9]+(?:-[a-z0-9]+)*` without a quantifier
 * inside a quantifier, so the engine does linear work on any input and ReDoS
 * scanners have nothing to flag.
 */
export const SLUG_PATTERN = /^(?!-)(?!.*--)[a-z0-9-]*[a-z0-9]$/;
export const LESSON_ID_PATTERN = /^l[1-9]-m[1-9]-(?!-)(?!.*--)[a-z0-9-]*[a-z0-9]$/;
export const CHECK_ID_PATTERN = /^l[1-9]-m[1-9]-(?!-)(?!.*--)[a-z0-9-]*[a-z0-9]-c[0-9]{2}$/;
export const STEP_ID_PATTERN = /^[1-9][0-9]?$/;
/** `family` or `family.descriptor`, each a slug of letters: at most one dot, never beside a hyphen. */
export const DRILL_ITEM_ID_PATTERN = /^(?![-.])(?!.*(?:--|-\.|\.-|\.\.))(?!.*\..*\.)[a-z.-]*[a-z]$/;
export const LEVEL_ID_PATTERN = /^[1-9]$/;
export const TEMPLATE_ID_PATTERN = SLUG_PATTERN;

export const LESSON_ID_MAX = 64;
export const CHECK_ID_MAX = 68;
export const DRILL_ITEM_ID_MAX = 64;
export const TEMPLATE_ID_MAX = 64;
/** A hard bound on the whole key, even though its parts already bound it lower. */
export const PROGRESS_KEY_MAX = 128;

export type ParsedProgressKey =
  | { readonly kind: "step"; readonly lessonId: string; readonly stepId: number }
  | { readonly kind: "check"; readonly checkId: string; readonly lessonId: string }
  | { readonly kind: "drill"; readonly itemId: string }
  | { readonly kind: "level"; readonly levelId: number }
  | { readonly kind: "shop"; readonly lineKey: string }
  | { readonly kind: "track"; readonly trackId: string }
  | { readonly kind: "badge"; readonly badgeId: string; readonly weekId: string }
  | { readonly kind: "mockweek"; readonly weekId: string }
  | { readonly kind: "research"; readonly promptId: string; readonly n: number };

/** The four specialisations of Level 4's mastery (ticket 219); a track opens from Level 3. */
export const TRACK_IDS = ["sommelier", "mw", "viticulturist", "oenologist"] as const;
export type TrackId = (typeof TRACK_IDS)[number];
export const isTrackId = (value: unknown): value is TrackId =>
  typeof value === "string" && (TRACK_IDS as readonly string[]).includes(value);

/** A mock week is named by its first local day: `mw-2026-10-26`. */
export const isMockWeekId = (value: string): boolean =>
  value.length === 13 && value.startsWith("mw-") && isUtcDay(value.slice(3));

/** Shape of a track or badge id: a short slug. Whether it names a real one is the edge's 422, not a 400. */
const KIND_ID_MAX = 32;
const isKindId = (value: string): boolean =>
  value.length <= KIND_ID_MAX && SLUG_PATTERN.test(value);

/** The lesson id prefix of each track: `som-m1-…`, `mw-m3-…`, `tv-…`, `to-…`; modules `m1` to `m9`. */
export const TRACK_LESSON_PREFIX: Readonly<Record<TrackId, string>> = {
  sommelier: "som",
  mw: "mw",
  viticulturist: "tv",
  oenologist: "to",
};
const TRACK_OF_PREFIX = new Map<string, TrackId>(
  TRACK_IDS.map((track) => [TRACK_LESSON_PREFIX[track], track]),
);
const LESSON_TAIL = /^-m[1-9]-(?!-)(?!.*--)[a-z0-9-]*[a-z0-9]$/;

/** The track a lesson id belongs to, or undefined for a level lesson or anything else. */
export function trackOfLesson(lessonId: string): TrackId | undefined {
  const dash = lessonId.indexOf("-");
  if (dash <= 0 || lessonId.length > LESSON_ID_MAX) return undefined;
  const track = TRACK_OF_PREFIX.get(lessonId.slice(0, dash));
  return track !== undefined && LESSON_TAIL.test(lessonId.slice(dash)) ? track : undefined;
}

/**
 * The key prefixes of a track lesson's steps and checks. A bundle from before ticket 219 cannot
 * parse such a key, so the edge sends it only to a client that said `?mastery=1`.
 */
export const TRACK_LESSON_KEY_PREFIXES: readonly string[] = ["step", "check"].flatMap((kind) =>
  TRACK_IDS.map((track) => `${kind}:${TRACK_LESSON_PREFIX[track]}-`),
);

export const isTrackLessonKey = (key: string): boolean =>
  TRACK_LESSON_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

export const trackKey = (trackId: TrackId): string => `track:${trackId}`;
export const badgeKey = (badgeId: BadgeId, weekId: string): string => `badge:${badgeId}:${weekId}`;
/** A research task is named by its quarter: `rp-2026-q4`. The n-th submission of it is its own item. */
export const RESEARCH_PROMPT_PATTERN = /^rp-\d{4}-q[1-4]$/;
export const researchKey = (promptId: string, n: number): string =>
  `research:${promptId}:${String(n)}`;
export const mockWeekKey = (weekId: string): string => `mockweek:${weekId}`;

export const stepKey = (lessonId: string, stepId: number): string =>
  `step:${lessonId}:${String(stepId)}`;
export const checkKey = (checkId: string): string => `check:${checkId}`;
export const drillKey = (itemId: string): string => `drill:${itemId}`;
export const levelKey = (levelId: number): string => `level:${String(levelId)}`;

const CHECK_TAIL = /-c[0-9]{2}$/;
function isCheckId(id: string): boolean {
  if (CHECK_ID_PATTERN.test(id)) return true;
  return CHECK_TAIL.test(id) && trackOfLesson(id.slice(0, -"-c00".length)) !== undefined;
}

/** A level lesson or a track lesson: the grammar of what a progress key may name. */
function isLessonId(id: string): boolean {
  return (
    id.length <= LESSON_ID_MAX && (LESSON_ID_PATTERN.test(id) || trackOfLesson(id) !== undefined)
  );
}

/** Reads a progress key, or `undefined` when it breaks the grammar (a 400 on the wire). */
export function parseProgressKey(key: string): ParsedProgressKey | undefined {
  if (key.length > PROGRESS_KEY_MAX) return undefined;
  const colon = key.indexOf(":");
  if (colon <= 0) return undefined;
  const prefix = key.slice(0, colon);
  const rest = key.slice(colon + 1);

  switch (prefix) {
    case "step": {
      const split = rest.lastIndexOf(":");
      if (split <= 0) return undefined;
      const lessonId = rest.slice(0, split);
      const stepId = rest.slice(split + 1);
      if (!isLessonId(lessonId) || !STEP_ID_PATTERN.test(stepId)) return undefined;
      return { kind: "step", lessonId, stepId: Number(stepId) };
    }
    case "check":
      if (rest.length > CHECK_ID_MAX || !isCheckId(rest)) return undefined;
      return { kind: "check", checkId: rest, lessonId: lessonIdOfCheck(rest) };
    case "drill":
      if (rest.length > DRILL_ITEM_ID_MAX || !DRILL_ITEM_ID_PATTERN.test(rest)) return undefined;
      return { kind: "drill", itemId: rest };
    case "level":
      if (!LEVEL_ID_PATTERN.test(rest)) return undefined;
      return { kind: "level", levelId: Number(rest) };
    case "shop":
      return isShopLineKey(rest) ? { kind: "shop", lineKey: rest } : undefined;
    case "track":
      return isKindId(rest) ? { kind: "track", trackId: rest } : undefined;
    case "badge": {
      const split = rest.indexOf(":");
      const badgeId = rest.slice(0, split);
      const weekId = rest.slice(split + 1);
      return split > 0 && isKindId(badgeId) && isMockWeekId(weekId)
        ? { kind: "badge", badgeId, weekId }
        : undefined;
    }
    case "research": {
      const split = rest.lastIndexOf(":");
      const promptId = rest.slice(0, split);
      const n = rest.slice(split + 1);
      return split > 0 && RESEARCH_PROMPT_PATTERN.test(promptId) && /^[1-9]$/.test(n)
        ? { kind: "research", promptId, n: Number(n) }
        : undefined;
    }
    case "mockweek":
      return isMockWeekId(rest) ? { kind: "mockweek", weekId: rest } : undefined;
    default:
      return undefined;
  }
}

/** `l1-m2-white-styles-c05` belongs to `l1-m2-white-styles`. */
export function lessonIdOfCheck(checkId: string): string {
  return checkId.slice(0, -"-c00".length);
}

/** The level digit of a lesson id: `l2-m4-…` is level 2. */
export function levelOfLesson(lessonId: string): number {
  return Number(lessonId.slice(1, 2));
}

/** The module of a lesson id: `l2-m4-…` is `m4`. */
export function moduleOfLesson(lessonId: string): string {
  return lessonId.slice(3, 5);
}

/** Aroma items are lexicon descriptors (`citrus.lime`), marker items ladder axes (`acidity`). */
export function drillKindOf(itemId: string): "aroma" | "marker" | undefined {
  if (isMarkerAxis(itemId)) return "marker";
  return itemId.includes(".") ? "aroma" : undefined;
}
